import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import { Voyageurs } from '../models/Voyageurs';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormPapouasieNG: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres de la route
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const { demandeId, title, prixconsulat, prixintervention, url_flag, validite, informations } = state;
  const isEditMode = demandeId != null;

  // États hérités de la route
  const [visaTitle] = useState<string>(title || '');
  const [prixConsulat] = useState<number>(prixconsulat || 0);
  const [prixIntervention] = useState<number>(prixintervention || 0);
  const [urlFlag] = useState<string>(url_flag || '');
  const [visaValidite] = useState<string>(validite || '');
  const commentaires = state?.commentaires || '';
  const [visaInfos] = useState<string>(informations || '');

  // États communs
  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);

  // États spécifiques au formulaire Papouasie-NG
  const [adresseHebergement, setAdresseHebergement] = useState<string>('');
  const [codePostalHebergement, setCodePostalHebergement] = useState<string>('');
  const [villeHebergement, setVilleHebergement] = useState<string>('');
  const [dejaVisite, setDejaVisite] = useState<string>('');
  const [dateDerniereVisite, setDateDerniereVisite] = useState<string>('');
  const [dureeDerniereVisite, setDureeDerniereVisite] = useState<string>('');
  const [butDerniereVisite, setButDerniereVisite] = useState<string>('');

  // Gestion des fichiers
  const [lettreInvitationFile, setLettreInvitationFile] = useState<File | null>(null);
  const [releveBancaireFile, setReleveBancaireFile] = useState<File | null>(null);
  const [billetRetourFile, setBilletRetourFile] = useState<File | null>(null);
  const [lettreMotivationFile, setLettreMotivationFile] = useState<File | null>(null);

  // Pour prévisualisation des fichiers
  const [lettreInvitationPreview, setLettreInvitationPreview] = useState<string | null>(null);
  const [releveBancairePreview, setReleveBancairePreview] = useState<string | null>(null);
  const [billetRetourPreview, setBilletRetourPreview] = useState<string | null>(null);
  const [lettreMotivationPreview, setLettreMotivationPreview] = useState<string | null>(null);

  // Validation du formulaire
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Calcul du total
  const total = prixConsulat + prixIntervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction utilitaire de conversion de date (jj/mm/aaaa)
  const parseDate = (dateStr: string): Date | null => {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return null;
  };

  const validateForm = () => {
    const errors: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    // Contrôle des dates
    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('papouasieNG.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('papouasieNG.errors.departureDateRequired'));
    }

    if (dateSortie) {
      if (sortieDate) {
        if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
          errors.push(tp('papouasieNG.errors.returnDateInvalid'));
        }
      } else {
        errors.push(tp('papouasieNG.errors.returnDateRequired'));
      }
    } else {
      errors.push(tp('papouasieNG.errors.returnDateRequiredMissing'));
    }

    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    if (!adresseHebergement.trim()) errors.push(tp('papouasieNG.validation.adresseHebergement'));
    if (!codePostalHebergement.trim()) errors.push(tp('papouasieNG.validation.codePostalHebergement'));
    if (!villeHebergement.trim()) errors.push(tp('papouasieNG.validation.villeHebergement'));

    // Pour visa affaire, la lettre d'invitation est obligatoire
    if (visaTitle.toLowerCase().includes('affaire')) {
      if (!lettreInvitationFile) {
        errors.push(tp('papouasieNG.validation.lettreInvitation'));
      }
    }

    // Pour visa tourisme, les documents suivants sont obligatoires
    if (visaTitle.toLowerCase().includes('tourisme')) {
      if (!releveBancaireFile) errors.push(tp('papouasieNG.validation.releveBancaire'));
      if (!billetRetourFile) errors.push(tp('papouasieNG.validation.billetRetour'));
      if (!lettreMotivationFile) errors.push(tp('papouasieNG.validation.lettreMotivation'));
    }

    if (!dejaVisite) errors.push(tp('papouasieNG.validation.dejaVisite'));
    if (dejaVisite === 'Oui') {
      if (!dateDerniereVisite.trim()) errors.push(tp('papouasieNG.validation.dateDerniereVisite'));
      if (!dureeDerniereVisite.trim()) errors.push(tp('papouasieNG.validation.dureeDerniereVisite'));
      if (!butDerniereVisite.trim()) errors.push(tp('papouasieNG.validation.butDerniereVisite'));
    }

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
    adresseHebergement,
    codePostalHebergement,
    villeHebergement,
    lettreInvitationFile,
    releveBancaireFile,
    billetRetourFile,
    lettreMotivationFile,
    dejaVisite,
    dateDerniereVisite,
    dureeDerniereVisite,
    butDerniereVisite,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find((v) => v.id.toString() === selectedVoyageur);

  // Empêche la fermeture accidentelle de l’onglet
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleBack = () => {
    if (window.confirm(t('common.confirmation.unsavedChanges'))) {
      navigate(-1);
    }
  };

  // Gestion des uploads de fichiers (lettre d'invitation, relevé bancaire, billet retour, lettre de motivation)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      if (type === 'lettreInvitation') {
        setLettreInvitationFile(file);
        setLettreInvitationPreview(preview);
      } else if (type === 'releveBancaire') {
        setReleveBancaireFile(file);
        setReleveBancairePreview(preview);
      } else if (type === 'billetRetour') {
        setBilletRetourFile(file);
        setBilletRetourPreview(preview);
      } else if (type === 'lettreMotivation') {
        setLettreMotivationFile(file);
        setLettreMotivationPreview(preview);
      }
    }
  };

  // Formatage de la date en jj/mm/aaaa
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += numbers[i];
    }
    return formatted;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId = '';
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }
      const etatFormulaire = champsNonRemplis.length === 0;
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId);
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', visaTitle);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixConsulat));
      formData.append('prixintervention', String(prixIntervention));
      formData.append('validite', visaValidite);
      formData.append('adresseHebergement', adresseHebergement);
      formData.append('codePostalHebergement', codePostalHebergement);
      formData.append('villeHebergement', villeHebergement);
      formData.append('dejaVisite', dejaVisite);
      formData.append('dateDerniereVisite', dateDerniereVisite);
      formData.append('dureeDerniereVisite', dureeDerniereVisite);
      formData.append('butDerniereVisite', butDerniereVisite);

      // Ajout des fichiers selon le type de visa
      if (visaTitle.toLowerCase().includes('affaire') && lettreInvitationFile) {
        formData.append('lettreInvitation', lettreInvitationFile);
      }
      if (visaTitle.toLowerCase().includes('tourisme')) {
        if (releveBancaireFile) formData.append('releveBancaire', releveBancaireFile);
        if (billetRetourFile) formData.append('billetRetour', billetRetourFile);
        if (lettreMotivationFile) formData.append('lettreMotivation', lettreMotivationFile);
      }

      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error("Erreur lors de la validation de la demande:", error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (réinitialiser le formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setAdresseHebergement('');
    setCodePostalHebergement('');
    setVilleHebergement('');
    setDejaVisite('');
    setDateDerniereVisite('');
    setDureeDerniereVisite('');
    setButDerniereVisite('');
    setLettreInvitationFile(null);
    setLettreInvitationPreview(null);
    setReleveBancaireFile(null);
    setReleveBancairePreview(null);
    setBilletRetourFile(null);
    setBilletRetourPreview(null);
    setLettreMotivationFile(null);
    setLettreMotivationPreview(null);
  };

  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {visaValidite}
              <br />
              {visaInfos}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map((v) => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Champs hébergement */}
          <TextField
            label={tp('papouasieNG.fields.adresseHebergement')}
            placeholder={tp('papouasieNG.fields.adresseHebergement')}
            value={adresseHebergement}
            onChange={(e) => setAdresseHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('papouasieNG.fields.codePostalHebergement')}
            placeholder={tp('papouasieNG.fields.codePostalHebergement')}
            value={codePostalHebergement}
            onChange={(e) => setCodePostalHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('papouasieNG.fields.villeHebergement')}
            placeholder={tp('papouasieNG.fields.villeHebergement')}
            value={villeHebergement}
            onChange={(e) => setVilleHebergement(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Avez-vous déjà visité la Papouasie-Nouvelle-Guinée ? */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="dejaVisite-label">
              {tp('papouasieNG.fields.dejaVisite')}
            </InputLabel>
            <Select
              labelId="dejaVisite-label"
              id="dejaVisite-select"
              value={dejaVisite}
              label={tp('papouasieNG.fields.dejaVisite')}
              onChange={(e: SelectChangeEvent) => setDejaVisite(e.target.value)}
            >
              <MenuItem value="Oui">{tp('papouasieNG.options.oui')}</MenuItem>
              <MenuItem value="Non">{tp('papouasieNG.options.non')}</MenuItem>
            </Select>
          </FormControl>

          {dejaVisite === 'Oui' && (
            <>
              <TextField
                label={tp('papouasieNG.fields.dateDerniereVisite.label')}
                placeholder={tp('papouasieNG.fields.dateDerniereVisite.placeholder')}
                value={dateDerniereVisite}
                onChange={(e) => setDateDerniereVisite(formatDate(e.target.value))}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('papouasieNG.fields.dureeDerniereVisite')}
                placeholder={tp('papouasieNG.fields.dureeDerniereVisite')}
                value={dureeDerniereVisite}
                onChange={(e) => setDureeDerniereVisite(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('papouasieNG.fields.butDerniereVisite')}
                placeholder={tp('papouasieNG.fields.butDerniereVisite')}
                value={butDerniereVisite}
                onChange={(e) => setButDerniereVisite(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          )}

          {/* Uploads de fichiers pour visa affaire */}
          {visaTitle.toLowerCase().includes('affaire') && (
            <Box sx={{ my: 2 }}>
              <Typography variant="body1" gutterBottom>
                {tp('papouasieNG.fields.lettreInvitation')}
              </Typography>
              <input type="file" accept="image/*,application/pdf" onChange={(e) => handleFileUpload(e, 'lettreInvitation')} />
              {lettreInvitationPreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={lettreInvitationPreview}
                    alt={tp('papouasieNG.alt.lettreInvitation')}
                    style={{ maxWidth: '100%', maxHeight: 300 }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Uploads de fichiers pour visa tourisme */}
          {visaTitle.toLowerCase().includes('tourisme') && (
            <>
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('papouasieNG.fields.releveBancaire')}
                </Typography>
                <input type="file" accept="image/*,application/pdf" onChange={(e) => handleFileUpload(e, 'releveBancaire')} />
                {releveBancairePreview && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={releveBancairePreview}
                      alt={tp('papouasieNG.alt.releveBancaire')}
                      style={{ maxWidth: '100%', maxHeight: 300 }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('papouasieNG.fields.billetRetour')}
                </Typography>
                <input type="file" accept="image/*,application/pdf" onChange={(e) => handleFileUpload(e, 'billetRetour')} />
                {billetRetourPreview && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={billetRetourPreview}
                      alt={tp('papouasieNG.alt.billetRetour')}
                      style={{ maxWidth: '100%', maxHeight: 300 }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('papouasieNG.fields.lettreMotivation')}
                </Typography>
                <input type="file" accept="image/*,application/pdf" onChange={(e) => handleFileUpload(e, 'lettreMotivation')} />
                {lettreMotivationPreview && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={lettreMotivationPreview}
                      alt={tp('papouasieNG.alt.lettreMotivation')}
                      style={{ maxWidth: '100%', maxHeight: 300 }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Module Assurance (identique à FormInde) */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixConsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixIntervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* État du formulaire */}
          <Box sx={{ my: 2, p: 2, borderRadius: 1, backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc' }}>
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormPapouasieNG;
